import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Field, Flex, Input, VStack, Text, Checkbox, Link, Heading } from "@chakra-ui/react";

import { authTexts } from "../i18n/login";
import { LanguageContext } from "../contexts/LanguageContext";
import { resumePendingReserve } from "../services/pendingReserve";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const labelStyles = {
  fontFamily: "space",
  fontSize: "xs",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "gray.600",
};

const inputStyles = {
  bg: "white",
  borderColor: "gray.300",
  color: "gray.900",
  _placeholder: { color: "gray.400" },
  _focusVisible: { borderColor: "gray.900", boxShadow: "none" },
};

const LoginForm = ({ setIsLoggedIn }) => {
  const { language } = useContext(LanguageContext);
  const t = authTexts[language] || authTexts["et"];

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [csrfToken, setCsrfToken] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);

  // Fetch CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/get_csrf_token/`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    fetchCsrfToken();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });

      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch { /* non-JSON error body */ }
        if (body?.error === 'account_not_activated') {
          setErrors({ error: t.accountNotActivated });
          return;
        }
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Set the state to logged in, and redirect user based on response
      const data = await response.json();

      setIsLoggedIn(true);
      // Continue with a remembered instrument selection when there is one
      const resumed = await resumePendingReserve(navigate);
      if (!resumed) navigate(data.redirect);

    } catch (error) {
      console.error('Login error:', error);
      setErrors({ error: t.loginFailed });
    }
  };

  const sendGoogleAuth = async (body) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      setIsLoggedIn(true);
      const resumed = await resumePendingReserve(navigate);
      if (!resumed) navigate(data.redirect);
    } catch (error) {
      console.error('Google login error:', error);
      setErrors({ error: t.loginFailed });
    }
  };

  // Custom Google button (OAuth code flow, popup): Google's own widget can't
  // change language at runtime — its label follows the gsi script load — so
  // we use our own fully localized button and exchange the code server-side.
  const googleClient = useRef(null);
  const handleGoogleClick = () => {
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) {
      setErrors({ error: t.loginFailed });
      return;
    }
    if (!googleClient.current) {
      googleClient.current = oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup',
        callback: (response) => {
          if (response.code) {
            sendGoogleAuth({ code: response.code });
          } else {
            setErrors({ error: t.loginFailed });
          }
        },
      });
    }
    googleClient.current.requestCode();
  };

  return (
    <Box minH="calc(100vh - 4rem)" display="flex" justifyContent="center" alignItems="flex-start" bg="gray.50" pt={{ base: 10, md: 20 }} px={4}>
      <Box
        w={{ base: "100%", sm: "420px" }}
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            <Box textAlign="center" mb={2}>
              <Text
                fontFamily="space"
                fontSize="xs"
                letterSpacing="0.3em"
                textTransform="uppercase"
                color="gray.500"
                mb={2}
              >
                akordion.ee
              </Text>
              <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900">
                {t.signIn}
              </Heading>
            </Box>

            <Field.Root invalid={!!errors.username}>
              <Field.Label {...labelStyles}>{t.usernameOrEmail}</Field.Label>
              <Input
                {...inputStyles}
                placeholder={t.enterusername}
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleInputChange}
                type="text"
              />
              {errors.username && <Field.ErrorText>{errors.username}</Field.ErrorText>}
            </Field.Root>

            <Field.Root invalid={!!errors.password}>
              <Field.Label {...labelStyles}>{t.password}</Field.Label>
              <Input
                {...inputStyles}
                placeholder={t.enterpassword}
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                type="password"
              />
              {errors.password && <Field.ErrorText>{errors.password}</Field.ErrorText>}
            </Field.Root>

            <Text fontSize="xs" textAlign="right" mt={-3}>
              <Link
                href={`${process.env.REACT_APP_SHOP_URL}/forgot-password`}
                color="gray.500"
                textDecoration="underline"
                textUnderlineOffset="3px"
                _hover={{ color: "gray.700" }}
              >
                {t.forgotPassword}
              </Link>
            </Text>

            <Checkbox.Root
              checked={rememberMe}
              onCheckedChange={(e) => setRememberMe(e.checked)}
              colorPalette="gray"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label color="gray.700" fontSize="sm">
                {t.remember}
              </Checkbox.Label>
            </Checkbox.Root>

            <Button
              type="submit"
              w="full"
              size="lg"
              bg="#09090b"
              color="white"
              borderRadius="sm"
              fontFamily="space"
              fontSize="xs"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
            >
              {t.signIn}
            </Button>

            {errors.error && (
              <Text color="red.500" fontSize="sm" textAlign="center">
                {errors.error}
              </Text>
            )}

            <Flex align="center" gap={3}>
              <Box flex="1" h="1px" bg="gray.200" />
              <Text fontFamily="space" fontSize="xs" textTransform="uppercase" letterSpacing="0.14em" color="gray.400">
                {t.orDivider}
              </Text>
              <Box flex="1" h="1px" bg="gray.200" />
            </Flex>

            <Button
              onClick={handleGoogleClick}
              variant="outline"
              size="lg"
              w="full"
              borderColor="gray.300"
              color="gray.800"
              borderRadius="sm"
              fontFamily="space"
              fontSize="xs"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              _hover={{ bg: "gray.100" }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {t.googleSignIn}
            </Button>

            <Text fontSize="xs" color="gray.500" textAlign="center">
              {t.sharedAccountHint}
            </Text>

            <Text fontSize="sm" color="gray.600" textAlign="center">
              {t.noAccount}{" "}
              <Link
                as={RouterLink}
                to="/register"
                color="gray.900"
                fontWeight="medium"
                textDecoration="underline"
                textUnderlineOffset="3px"
                _hover={{ color: "gray.600" }}
              >
                {t.registerHere}
              </Link>
            </Text>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default LoginForm;
